/**
 * Session + weekly volume-profile levels (POC/VAH/VAL) per symbol, as the
 * engine computes them from raw ticks. Market data, so not persisted.
 *
 * Fed by the `volume-profile-levels` socket event (sent only when a level
 * changes) and seeded from GET /api/volume-profile-levels/:symbol.
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { CONFIG } from '../config';
import type { SessionType } from '../types/volumeProfile';

export interface ProfileLevelSet {
  poc: number;
  vah: number;
  val: number;
  start: number;
  totalVolume: number;
}

export interface ProfileLevels {
  sessionType: SessionType;
  valueAreaPercent: number;
  session: ProfileLevelSet | null;
  weekly: ProfileLevelSet | null;
}

interface ProfileLevelsState {
  bySymbol: Record<string, ProfileLevels | null>;
  setLevels: (symbol: string, levels: ProfileLevels | null) => void;
  fetchLevels: (symbol: string) => Promise<void>;
}

export const useProfileLevelsStore = create<ProfileLevelsState>((set) => ({
  bySymbol: {},
  setLevels: (symbol, levels) => set((s) => ({ bySymbol: { ...s.bySymbol, [symbol]: levels } })),
  fetchLevels: async (symbol) => {
    try {
      const r = await fetch(`${CONFIG.getBackendUrl()}/api/volume-profile-levels/${encodeURIComponent(symbol)}`);
      if (!r.ok) return;
      const d = await r.json();
      set((s) => ({ bySymbol: { ...s.bySymbol, [symbol]: d?.levels ?? null } }));
    } catch {
      /* engine may be booting; the socket event will fill it in */
    }
  },
}));

/** Display prefs for the key-level tags and the confluence zones. */
export interface KeyLevelPrefs {
  sessionProfile: boolean;
  weeklyProfile: boolean;
  confluence: boolean;
  /** Levels within this many points of each other form a zone. */
  confluenceTolerance: number;
}

export const DEFAULT_KEY_LEVEL_PREFS: KeyLevelPrefs = {
  sessionProfile: true,
  weeklyProfile: true,
  confluence: true,
  confluenceTolerance: 10,
};

interface KeyLevelPrefsState extends KeyLevelPrefs {
  toggle: (key: 'sessionProfile' | 'weeklyProfile' | 'confluence') => void;
  setConfluenceTolerance: (pts: number) => void;
}

export const clampTolerance = (pts: number) =>
  Number.isFinite(pts) ? Math.max(1, Math.min(50, Math.round(pts * 4) / 4)) : DEFAULT_KEY_LEVEL_PREFS.confluenceTolerance;

/** Per-key merge so prefs added later fall back to their defaults. */
export function mergeKeyLevelPrefs<T extends KeyLevelPrefs>(persisted: unknown, current: T): T {
  const p = (persisted && typeof persisted === 'object' ? persisted : {}) as Record<string, unknown>;
  const out = { ...current };
  for (const k of ['sessionProfile', 'weeklyProfile', 'confluence'] as const) {
    if (typeof p[k] === 'boolean') out[k] = p[k] as boolean;
  }
  if (typeof p.confluenceTolerance === 'number') out.confluenceTolerance = clampTolerance(p.confluenceTolerance);
  return out;
}

export const useKeyLevelPrefsStore = create<KeyLevelPrefsState>()(
  persist(
    (set) => ({
      ...DEFAULT_KEY_LEVEL_PREFS,
      toggle: (key) => set((s) => ({ [key]: !s[key] }) as Partial<KeyLevelPrefsState>),
      setConfluenceTolerance: (pts) => set({ confluenceTolerance: clampTolerance(pts) }),
    }),
    {
      name: 'key-level-prefs',
      partialize: (s) => ({
        sessionProfile: s.sessionProfile,
        weeklyProfile: s.weeklyProfile,
        confluence: s.confluence,
        confluenceTolerance: s.confluenceTolerance,
      }),
      merge: (persisted, current) => mergeKeyLevelPrefs(persisted, current),
    },
  ),
);
